import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ChevronRight, Heart, Users, MessageCircle, Bell, Loader2, Sparkles } from 'lucide-react';
import heroImage from "@/assets/hero-bg.jpg";
import { useTranslation } from '@/hooks/useTranslation';

interface OnboardingData {
  gender: 'male' | 'female' | 'non-binary' | null;
  age: number | null;
  interested_in: 'men' | 'women' | 'both' | null;
  name: string | null;
  looking_for: string[] | null;
  fulfillment_level: string | null;
  communication_style: string[] | null;
  notifications_allowed: boolean | null;
}

interface OnboardingFlowProps {
  onComplete: (data: { 
    gender: 'male' | 'female' | 'non-binary' | null; 
    age: number | null; 
    interested_in: 'men' | 'women' | 'both' | null; 
    name: string | null; 
    looking_for: string | null; 
    fulfillment_level: string | null; 
    communication_style: string | null;
    notifications_allowed: boolean | null 
  }) => void;
  onBack?: () => void;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete, onBack }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState<OnboardingData>({
    gender: null,
    age: null,
    interested_in: null,
    name: null,
    looking_for: null,
    fulfillment_level: null,
    communication_style: null,
    notifications_allowed: null
  });

  const handleNext = () => {
    if (currentStep === steps.length - 1) {
      // Last step (loading) - just start loading, completion handled by progress bar
      if (!isLoading) {
        setIsLoading(true);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const steps = [
    {
      title: t('onboarding.name_title'),
      subtitle: t('onboarding.name_title'),
      content: (
        <div className="space-y-4">
          <Input
            ref={nameInputRef}
            type="text"
            placeholder={t('onboarding.name_placeholder')}
            value={data.name || ''}
            onChange={(e) => setData({ ...data, name: e.target.value || null })}
            className="h-14 text-lg text-left bg-background/20 border-none"
          />
        </div>
      )
    },
    {
      title: t('onboarding.looking_for_title'),
      subtitle: t('onboarding.looking_for_subtitle'),
      content: (
        <div className="space-y-4">
          {[
            { value: 'someone-to-talk-to', label: t('onboarding.looking_for_talk') },
            { value: 'emotional-support', label: t('onboarding.looking_for_support') },
            { value: 'deep-conversations', label: t('onboarding.looking_for_conversation') },
            { value: 'romantic-connection', label: t('onboarding.looking_for_romantic') },
            { value: 'fun-and-entertainment', label: t('onboarding.looking_for_fun') }
          ].map((option) => (
            <Button
              key={option.value}
              variant={data.looking_for?.includes(option.value) ? "default" : "outline"}
              className={`w-full h-14 text-lg justify-start transition-all duration-200 ${
                data.looking_for?.includes(option.value) 
                  ? "bg-primary text-primary-foreground border-primary shadow-lg scale-105" 
                  : "bg-background/20 hover:bg-background/30 border-primary/20 text-foreground hover:text-primary"
              }`}
              onClick={() => {
                const currentSelections = data.looking_for || [];
                const newSelections = currentSelections.includes(option.value)
                  ? currentSelections.filter(item => item !== option.value)
                  : [...currentSelections, option.value];
                setData({ ...data, looking_for: newSelections });
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      )
    },
    {
      title: t('onboarding.fulfillment_title'),
      subtitle: t('onboarding.fulfillment_subtitle'),
      content: (
        <div className="space-y-4">
          {[
            { value: 'very-fulfilled', label: t('onboarding.fulfillment_very') },
            { value: 'somewhat-fulfilled', label: t('onboarding.fulfillment_somewhat') },
            { value: 'neutral', label: t('onboarding.fulfillment_neutral') },
            { value: 'somewhat-unfulfilled', label: t('onboarding.fulfillment_somewhat_un') },
            { value: 'very-unfulfilled', label: t('onboarding.fulfillment_very_un') }
          ].map((option) => (
            <Button
              key={option.value}
              variant={data.fulfillment_level === option.value ? "default" : "outline"}
              className="w-full h-14 text-lg justify-start bg-background/20 hover:bg-background/30 border-primary/20 text-foreground hover:text-primary transition-all duration-200"
              onClick={() => {
                setData({ ...data, fulfillment_level: option.value });
                setTimeout(() => handleNext(), 200);
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      )
    },
    {
      title: t('onboarding.communication_title'),
      subtitle: t('onboarding.communication_subtitle'),
      content: (
        <div className="space-y-4">
          {[
            { value: 'romantic', label: t('onboarding.communication_romantic') },
            { value: 'humorous', label: t('onboarding.communication_humorous') },
            { value: 'tender', label: t('onboarding.communication_tender') },
            { value: 'supportive', label: t('onboarding.communication_supportive') },
            { value: 'passionate', label: t('onboarding.communication_passionate') },
            { value: 'gentle', label: t('onboarding.communication_gentle') }
          ].map((option) => (
            <Button
              key={option.value}
              variant={data.communication_style?.includes(option.value) ? "default" : "outline"}
              className={`w-full h-14 text-lg justify-start transition-all duration-200 ${
                data.communication_style?.includes(option.value) 
                  ? "bg-primary text-primary-foreground border-primary shadow-lg scale-105" 
                  : "bg-background/20 hover:bg-background/30 border-primary/20 text-foreground hover:text-primary"
              }`}
              onClick={() => {
                const currentSelections = data.communication_style || [];
                const newSelections = currentSelections.includes(option.value)
                  ? currentSelections.filter(item => item !== option.value)
                  : [...currentSelections, option.value];
                setData({ ...data, communication_style: newSelections });
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      )
    },
    {
      title: t('onboarding.gender_title'),
      subtitle: t('onboarding.gender_title'),
      content: (
        <div className="space-y-4">
          {[
            { value: 'female', label: t('onboarding.gender_female') },
            { value: 'male', label: t('onboarding.gender_male') },
            { value: 'non-binary', label: t('onboarding.gender_non_binary') }
          ].map((option) => (
            <Button
              key={option.value}
              variant={data.gender === option.value ? "default" : "outline"}
              className="w-full h-14 text-lg justify-start bg-background/20 hover:bg-background/30 border-primary/20 text-foreground hover:text-primary transition-all duration-200"
              onClick={() => {
                setData({ ...data, gender: option.value as any });
                setTimeout(() => handleNext(), 200);
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      )
    },
    {
      title: t('onboarding.interested_title'),
      subtitle: t('onboarding.interested_subtitle'),
      content: (
        <div className="space-y-4">
          {[
            { value: 'men', label: t('onboarding.interested_men') },
            { value: 'women', label: t('onboarding.interested_women') },
            { value: 'both', label: t('onboarding.interested_both') }
          ].map((option) => (
            <Button
              key={option.value}
              variant={data.interested_in === option.value ? "default" : "outline"}
              className="w-full h-14 text-lg justify-start bg-background/20 hover:bg-background/30 border-primary/20 text-foreground hover:text-primary transition-all duration-200"
              onClick={() => {
                setData({ ...data, interested_in: option.value as any });
                setTimeout(() => handleNext(), 200);
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      )
    },
    {
      title: t('onboarding.notifications_title'),
      subtitle: t('onboarding.notifications_subtitle'),
      content: (
        <div className="space-y-4">
          {[
            { value: true, label: t('onboarding.notifications_yes') },
            { value: false, label: t('onboarding.notifications_no') }
          ].map((option) => (
            <Button
              key={option.value.toString()}
              variant={data.notifications_allowed === option.value ? "default" : "outline"}
              className="w-full h-14 text-lg justify-start bg-background/20 hover:bg-background/30 border-primary/20 text-foreground hover:text-primary transition-all duration-200"
              onClick={() => {
                setData({ ...data, notifications_allowed: option.value });
                setTimeout(() => handleNext(), 200);
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      )
    },
    {
      title: t('onboarding.loading_title'),
      subtitle: t('onboarding.loading_subtitle'),
      content: null // Will be rendered separately
    }
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 0: return data.name !== null && data.name.trim() !== '';
      case 1: return data.looking_for !== null && data.looking_for.length > 0;
      case 2: return data.fulfillment_level !== null;
      case 3: return data.communication_style !== null && data.communication_style.length > 0;
      case 4: return data.gender !== null;
      case 5: return data.interested_in !== null;
      case 6: return data.notifications_allowed !== null;
      case 7: return true; // Loading step
      default: return false;
    }
  };

  // Auto-proceed on loading step
  useEffect(() => {
    if (currentStep === 7 && !isLoading) {
      setTimeout(() => handleNext(), 1000);
    }
  }, [currentStep]);

  // Animate loading progress
  useEffect(() => {
    if (currentStep === 7) {
      setLoadingProgress(0);
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 2; // Increase by 2% every interval
        });
      }, 60); // Update every 60ms (3 seconds total)

      return () => clearInterval(interval);
    }
  }, [currentStep]);

  // Trigger completion when loading reaches 100%
  useEffect(() => {
    if (currentStep === 7 && loadingProgress >= 100 && isLoading) {
      const timer = setTimeout(() => {
        const transformedData = {
          ...data,
          looking_for: data.looking_for ? data.looking_for.join(', ') : null,
          communication_style: data.communication_style ? data.communication_style.join(', ') : null
        } as any;
        
        onComplete(transformedData);
      }, 500); // Small delay for smooth transition
      
      return () => clearTimeout(timer);
    }
  }, [loadingProgress, currentStep, isLoading]);

  // Auto-focus name input on name step
  useEffect(() => {
    if (currentStep === 0 && nameInputRef.current) {
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
  }, [currentStep]);

  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-dreamy" />
      <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
        backgroundImage: `url(${heroImage})`
      }} />
      
      {/* Main content area */}
      <div className={`relative z-20 flex-1 flex p-4 pt-8 ${currentStep === 7 ? 'items-center justify-center' : 'items-start justify-start'}`}>
        <div className="w-full max-w-2xl">
          {/* Progress indicator - only show for non-loading steps */}
          {currentStep < 7 && (
            <div className="flex mb-8 max-w-md mx-auto">
              <div className="w-full bg-muted h-2 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${Math.max(10, (currentStep / (steps.length - 1)) * 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Regular step content */}
          {currentStep < 7 && (
            <Card className="bg-transparent border-none">
              <CardContent className="pr-8 pb-8 pl-4">
                <h2 className="font-bold text-left mb-8 text-2xl">
                  💕 {steps[currentStep].title}
                </h2>
                <div className="max-w-md mx-auto">
                  {steps[currentStep].content}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loading step content */}
          {currentStep === 7 && (
            <Card className="bg-transparent border-none">
              <CardContent className="pr-8 pb-8 pl-4">
                <div className="max-w-md mx-auto">
                  <div className="flex flex-col items-center justify-center space-y-8 text-white">
                    <h2 className="text-4xl font-bold text-center mb-8">
                      💕 {steps[currentStep].title}
                    </h2>
                    
                    {/* Circular Progress Bar */}
                    <div className="relative w-32 h-32">
                      {/* Background circle */}
                      <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          stroke="rgba(255,255,255,0.2)"
                          strokeWidth="8"
                          fill="none"
                        />
                        {/* Progress circle with gradient */}
                        <defs>
                          <linearGradient id="purplePinkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="hsl(262, 83%, 58%)" />
                            <stop offset="50%" stopColor="hsl(320, 85%, 60%)" />
                            <stop offset="100%" stopColor="hsl(300, 80%, 65%)" />
                          </linearGradient>
                        </defs>
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          stroke="url(#purplePinkGradient)"
                          strokeWidth="8"
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 40}`}
                          strokeDashoffset={`${2 * Math.PI * 40 * (1 - loadingProgress / 100)}`}
                          className="transition-all duration-200 ease-out"
                          strokeLinecap="round"
                        />
                      </svg>
                      
                      {/* Percentage text */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">
                          {Math.round(loadingProgress)}%
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-center space-y-3 mt-6">
                      <p className="text-lg opacity-0 animate-fade-in" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>{t('onboarding.loading_analyzing')}</p>
                      <p className="text-lg opacity-0 animate-fade-in" style={{ animationDelay: '1.5s', animationFillMode: 'forwards' }}>{t('onboarding.loading_finding')}</p>
                      <p className="text-lg opacity-0 animate-fade-in" style={{ animationDelay: '2.5s', animationFillMode: 'forwards' }}>{t('onboarding.loading_customizing')}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Navigation buttons at bottom */}
      {currentStep < 7 && (
        <div className="relative z-20 px-6 pt-6 pb-12 -mt-16">
          <div className="flex justify-between max-w-2xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => {
                console.log('Back button clicked');
                onBack?.();
              }}
              className="bg-card/50 backdrop-blur-sm"
            >
              {t('onboarding.back')}
            </Button>
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              variant="dreamy"
              size="lg"
            >
              {currentStep === steps.length - 1 ? t('onboarding.complete') : t('onboarding.next')}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};